/**
 * Personality Color Test - Discover Your True Self
 */

import './styles.css';
import { ParticleBackground } from './particles';

// 性格类型定义
interface PersonalityType {
  name: string;
  color: string;
  description: string;
  traits: Array<{ title: string; description: string }>;
}

// 问题定义
interface Question {
  id: number;
  text: string;
  options: Array<{ text: string; scores: { red: number; blue: number; yellow: number; green: number } }>;
}

// Personality type data
const personalityTypes: Record<string, PersonalityType> = {
  red: {
    name: 'Flame Star',
    color: 'red',
    description: 'Power Type - Blazing like fire, determined goals, iron will, a natural-born leader',
    traits: [
      { title: '🔥 Blazing Leadership', description: 'Illuminates the path like flames, natural leadership qualities, inspires teams to push forward toward goals' },
      { title: '⚔️ Warrior Spirit', description: 'Never backs down from challenges, strong competitive awareness, unleashes even greater power under pressure' },
      { title: '🎯 Goal-Oriented', description: 'Strikes like an arrow toward targets, focuses on results and achievements, gives full effort to realize inner aspirations' },
      { title: '💪 Decisive & Firm', description: 'Speaks directly and frankly, acts decisively and powerfully, pursues efficiency, dislikes procrastination' }
    ]
  },
  blue: {
    name: 'Deep Sea Star',
    color: 'blue',
    description: 'Perfect Type - Profound like the deep sea, rational and rigorous, pursues excellence, attentive to every detail',
    traits: [
      { title: '💎 Light of Wisdom', description: 'Brilliant thinking like gemstones, excels at logical analysis and deep thinking, carefully weighs pros and cons before decisions' },
      { title: '🎨 Perfectionism', description: 'Extremely high standards for work and life, pursues quality and precision, tolerates no flaws' },
      { title: '📐 Systematic Planning', description: 'Enjoys creating detailed plans, completes tasks methodically, keeps everything well-organized' },
      { title: '🧠 Thoughtful Deliberation', description: 'Cautious and conservative, doesn\'t jump to conclusions, values facts and data, every step is deeply considered' }
    ]
  },
  yellow: {
    name: 'Radiant Star',
    color: 'yellow',
    description: 'Vibrant Type - Bright like sunshine, full of enthusiasm, brimming with creativity, social expert',
    traits: [
      { title: '⭐ Shining Brilliance', description: 'Always full of energy and enthusiasm, shines like stars, can infect and inspire everyone around' },
      { title: '🌈 Limitless Creativity', description: 'Mind active like shooting stars, excels at innovation, always comes up with surprising new ideas and approaches' },
      { title: '🎪 Social Genius', description: 'Natural socialite, exceptional charm, skilled at networking, makes friends wherever they go' },
      { title: '🦋 Flexible & Adaptable', description: 'Extremely adaptable, enjoys change and novelty, rejects monotonous routines' }
    ]
  },
  green: {
    name: 'Forest Star',
    color: 'green',
    description: 'Peaceful Type - Inclusive like forests, gentle and friendly, empathetic, the team\'s lubricant',
    traits: [
      { title: '🌿 Source of Harmony', description: 'Peaceful and serene like forests, values relationships, good at listening and understanding others, avoids conflict' },
      { title: '🕊️ Patient & Stable', description: 'Calm personality like flowing streams, patient, can persistently complete tasks, never impulsive' },
      { title: '🤝 Supportive of Others', description: 'Eager to help, excellent at teamwork, a reliable partner and trustworthy friend' },
      { title: '💚 Tolerant & Understanding', description: 'Broad-minded like the earth, can accept different viewpoints, skilled at mediating conflicts and resolving differences' }
    ]
  }
};

// Test questions
const questions: Question[] = [
  {
    id: 1,
    text: 'In team meetings, you usually:',
    options: [
      { text: 'Actively speak up and express your opinions and suggestions', scores: { red: 3, blue: 0, yellow: 2, green: 0 } },
      { text: 'Listen carefully and speak only when you have solid evidence', scores: { red: 0, blue: 3, yellow: 0, green: 1 } },
      { text: 'Actively interact and use humor to liven the atmosphere', scores: { red: 1, blue: 0, yellow: 3, green: 1 } },
      { text: 'Take notes carefully and prefer to share ideas privately', scores: { red: 0, blue: 1, yellow: 0, green: 3 } }
    ]
  },
  {
    id: 2,
    text: 'Facing a new project, your first reaction is:',
    options: [
      { text: 'Immediately create an action plan and start executing', scores: { red: 3, blue: 1, yellow: 1, green: 0 } },
      { text: 'Analyze requirements and risks in detail, develop a complete plan', scores: { red: 1, blue: 3, yellow: 0, green: 1 } },
      { text: 'Get excited and think of various innovative ideas', scores: { red: 1, blue: 0, yellow: 3, green: 0 } },
      { text: 'Understand team members\' thoughts and seek consensus', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 3,
    text: 'Your work style tends toward:',
    options: [
      { text: 'Efficient and fast, pursuing results', scores: { red: 3, blue: 0, yellow: 1, green: 0 } },
      { text: 'Systematic and rigorous, attentive to details', scores: { red: 0, blue: 3, yellow: 0, green: 1 } },
      { text: 'Flexible and innovative, enjoying the process', scores: { red: 1, blue: 0, yellow: 3, green: 1 } },
      { text: 'Stable and collaborative, harmonious and win-win', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 4,
    text: 'Under pressure, you usually:',
    options: [
      { text: 'Become more focused and rise to the challenge', scores: { red: 3, blue: 1, yellow: 0, green: 0 } },
      { text: 'Stay calm and seek the best solution', scores: { red: 1, blue: 3, yellow: 0, green: 1 } },
      { text: 'Remain optimistic and use humor to ease tension', scores: { red: 0, blue: 0, yellow: 3, green: 1 } },
      { text: 'Seek support and face it together with the team', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 5,
    text: 'Your preferred work environment is:',
    options: [
      { text: 'Full of challenges and competition', scores: { red: 3, blue: 0, yellow: 1, green: 0 } },
      { text: 'Quiet and orderly, where you can focus and think', scores: { red: 0, blue: 3, yellow: 0, green: 2 } },
      { text: 'Active and interesting, full of creative atmosphere', scores: { red: 1, blue: 0, yellow: 3, green: 1 } },
      { text: 'Harmonious and friendly, with a warm team atmosphere', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 6,
    text: 'When making decisions, you rely more on:',
    options: [
      { text: 'Intuition and experience, quick decision-making', scores: { red: 3, blue: 0, yellow: 1, green: 0 } },
      { text: 'Data and logical analysis', scores: { red: 1, blue: 3, yellow: 0, green: 1 } },
      { text: 'Creativity and possibilities', scores: { red: 1, blue: 0, yellow: 3, green: 0 } },
      { text: 'Others\' opinions and team consensus', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 7,
    text: 'How do friends describe you:',
    options: [
      { text: 'Strong-willed, decisive, opinionated', scores: { red: 3, blue: 0, yellow: 0, green: 0 } },
      { text: 'Rational, rigorous, reliable', scores: { red: 0, blue: 3, yellow: 0, green: 1 } },
      { text: 'Fun, enthusiastic, creative', scores: { red: 0, blue: 0, yellow: 3, green: 1 } },
      { text: 'Gentle, kind, easy to get along with', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 8,
    text: 'In conflicts, you tend to:',
    options: [
      { text: 'Face it directly and argue your case', scores: { red: 3, blue: 1, yellow: 0, green: 0 } },
      { text: 'Analyze the reasons and seek a reasonable solution', scores: { red: 1, blue: 3, yellow: 0, green: 1 } },
      { text: 'Use humor to defuse it and maintain a relaxed atmosphere', scores: { red: 0, blue: 0, yellow: 3, green: 1 } },
      { text: 'Avoid conflict and seek compromise and harmony', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 9,
    text: 'The value you cherish most is:',
    options: [
      { text: 'Achievement and victory', scores: { red: 3, blue: 0, yellow: 1, green: 0 } },
      { text: 'Accuracy and perfection', scores: { red: 0, blue: 3, yellow: 0, green: 1 } },
      { text: 'Happiness and freedom', scores: { red: 1, blue: 0, yellow: 3, green: 1 } },
      { text: 'Peace and stability', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  },
  {
    id: 10,
    text: 'On weekends, you prefer to:',
    options: [
      { text: 'Participate in competitive sports or achieve a goal', scores: { red: 3, blue: 0, yellow: 1, green: 0 } },
      { text: 'Learn new skills or organize and plan', scores: { red: 1, blue: 3, yellow: 0, green: 1 } },
      { text: 'Hang out with friends or try something new', scores: { red: 1, blue: 0, yellow: 3, green: 1 } },
      { text: 'Relax and enjoy quiet time', scores: { red: 0, blue: 1, yellow: 1, green: 3 } }
    ]
  }
];

// 应用状态
class AppState {
  currentScreen: 'welcome' | 'question' | 'result' = 'welcome';
  currentQuestionIndex = 0;
  answers: number[] = [];
  scores = { red: 0, blue: 0, yellow: 0, green: 0 };

  reset() {
    this.currentScreen = 'welcome';
    this.currentQuestionIndex = 0;
    this.answers = [];
    this.scores = { red: 0, blue: 0, yellow: 0, green: 0 };
  }

  selectAnswer(optionIndex: number) {
    this.answers[this.currentQuestionIndex] = optionIndex;
  }

  nextQuestion() {
    if (this.currentQuestionIndex < questions.length - 1) {
      this.currentQuestionIndex++;
      return true;
    }
    return false;
  }

  previousQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
      return true;
    }
    return false;
  }

  calculateResults() {
    this.scores = { red: 0, blue: 0, yellow: 0, green: 0 };
    this.answers.forEach((answerIndex, questionIndex) => {
      const question = questions[questionIndex];
      const selectedOption = question.options[answerIndex];
      this.scores.red += selectedOption.scores.red;
      this.scores.blue += selectedOption.scores.blue;
      this.scores.yellow += selectedOption.scores.yellow;
      this.scores.green += selectedOption.scores.green;
    });
  }

  getPrimaryType(): string {
    const entries = Object.entries(this.scores);
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0];
  }
}

const state = new AppState();

// Particle background instance
let particleBackground: ParticleBackground | null = null;

// 创建背景动画
function createBackground(): HTMLElement {
  const bg = document.createElement('div');
  bg.className = 'bg-animation';

  // Create canvas for particles
  const canvas = document.createElement('canvas');
  canvas.className = 'particle-canvas';
  canvas.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
  `;
  bg.appendChild(canvas);

  // Initialize particle system
  if (particleBackground) {
    particleBackground.stop();
  }
  particleBackground = new ParticleBackground(canvas);
  particleBackground.start();

  return bg;
}

// 创建欢迎屏幕
function createWelcomeScreen(): HTMLElement {
  const screen = document.createElement('div');
  screen.className = 'welcome-screen';

  const title = document.createElement('h1');
  title.className = 'welcome-title';
  title.textContent = 'Celestial Personality Divination';

  const subtitle = document.createElement('p');
  subtitle.className = 'welcome-subtitle';
  subtitle.textContent = '✨ Explore the Color Code Deep Within Your Soul ✨';

  const description = document.createElement('p');
  description.className = 'welcome-description';
  description.textContent = 'In the vast starry sea, every soul shines with its unique light. Through 10 mysterious celestial questions, let ancient color divination reveal your true inner personality. Four celestial forces will guide your destiny\'s direction.';

  const badges = document.createElement('div');
  badges.className = 'color-badges';

  const colors = [
    { name: 'Flame Star', color: 'red', emoji: '🔥' },
    { name: 'Deep Sea Star', color: 'blue', emoji: '💎' },
    { name: 'Radiant Star', color: 'yellow', emoji: '⭐' },
    { name: 'Forest Star', color: 'green', emoji: '🌿' }
  ];

  colors.forEach(({ name, color, emoji }) => {
    const badge = document.createElement('div');
    badge.className = `color-badge ${color}`;
    badge.innerHTML = `<span>${emoji}</span><span>${name}</span>`;
    badges.appendChild(badge);
  });

  const startBtn = document.createElement('button');
  startBtn.className = 'start-button';
  startBtn.textContent = '🔮 Begin Divination 🔮';
  startBtn.onclick = () => {
    state.currentScreen = 'question';
    render();
  };

  screen.appendChild(title);
  screen.appendChild(subtitle);
  screen.appendChild(description);
  screen.appendChild(badges);
  screen.appendChild(startBtn);

  return screen;
}

// 创建问题屏幕
function createQuestionScreen(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'container';

  const screen = document.createElement('div');
  screen.className = 'question-screen';

  // 进度条
  const progressBar = document.createElement('div');
  progressBar.className = 'progress-bar';
  const progressFill = document.createElement('div');
  progressFill.className = 'progress-fill';
  progressFill.style.width = `${((state.currentQuestionIndex + 1) / questions.length) * 100}%`;
  progressBar.appendChild(progressFill);

  // Question number
  const questionNumber = document.createElement('div');
  questionNumber.className = 'question-number';
  questionNumber.textContent = `Question ${state.currentQuestionIndex + 1} / ${questions.length}`;

  // Add hint text
  const hint = document.createElement('div');
  hint.className = 'question-hint';
  hint.innerHTML = '✨ Will auto-advance to next question after selection';
  hint.style.cssText = `
    text-align: center;
    color: var(--color-text-dim);
    font-size: 0.9rem;
    margin-bottom: 20px;
    opacity: 0.7;
  `;

  // 问题卡片
  const questionCard = document.createElement('div');
  questionCard.className = 'question-card';

  const question = questions[state.currentQuestionIndex];
  const questionText = document.createElement('div');
  questionText.className = 'question-text';
  questionText.textContent = question.text;

  const options = document.createElement('div');
  options.className = 'options';

  const letters = ['A', 'B', 'C', 'D'];
  question.options.forEach((option, index) => {
    const optionEl = document.createElement('div');
    optionEl.className = 'option';
    optionEl.dataset.index = index.toString();
    if (state.answers[state.currentQuestionIndex] === index) {
      optionEl.classList.add('selected');
    }

    // Card container
    const card = document.createElement('div');
    card.className = 'option-card';

    // Card front
    const cardFront = document.createElement('div');
    cardFront.className = 'option-card-front';

    // Particles decoration
    const particles = document.createElement('div');
    particles.className = 'card-particles';
    cardFront.appendChild(particles);

    // Letter badge
    const letter = document.createElement('div');
    letter.className = 'option-letter';
    letter.textContent = letters[index];

    // Option text
    const text = document.createElement('div');
    text.className = 'option-text';
    text.textContent = option.text;

    cardFront.appendChild(letter);
    cardFront.appendChild(text);
    card.appendChild(cardFront);
    optionEl.appendChild(card);

    optionEl.onclick = () => {
      // 更新状态
      state.selectAnswer(index);

      // 只更新选项的选中状态，不重新渲染整个页面
      const allOptions = options.querySelectorAll('.option');
      allOptions.forEach(opt => opt.classList.remove('selected'));
      optionEl.classList.add('selected');

      // 启用下一题按钮
      nextBtn.disabled = false;

      // 延迟后自动进入下一题
      setTimeout(() => {
        const isLastQuestion = state.currentQuestionIndex === questions.length - 1;
        if (isLastQuestion) {
          state.calculateResults();
          state.currentScreen = 'result';
          render();
        } else {
          state.nextQuestion();
          render();
        }
      }, 600); // 600ms 后自动跳转，让用户看到选中效果
    };

    options.appendChild(optionEl);
  });

  questionCard.appendChild(questionText);
  questionCard.appendChild(options);

  // 导航按钮
  const navButtons = document.createElement('div');
  navButtons.className = 'nav-buttons';

  const prevBtn = document.createElement('button');
  prevBtn.className = 'nav-button';
  prevBtn.textContent = 'Previous';
  prevBtn.disabled = state.currentQuestionIndex === 0;
  prevBtn.onclick = () => {
    if (state.previousQuestion()) {
      render();
    }
  };

  const nextBtn = document.createElement('button');
  nextBtn.className = 'nav-button primary';
  const isLastQuestion = state.currentQuestionIndex === questions.length - 1;
  nextBtn.textContent = isLastQuestion ? 'View Results' : 'Next';
  nextBtn.disabled = state.answers[state.currentQuestionIndex] === undefined;
  nextBtn.onclick = () => {
    if (isLastQuestion) {
      state.calculateResults();
      state.currentScreen = 'result';
      render();
    } else {
      state.nextQuestion();
      render();
    }
  };

  navButtons.appendChild(prevBtn);
  navButtons.appendChild(nextBtn);

  screen.appendChild(progressBar);
  screen.appendChild(questionNumber);
  screen.appendChild(hint);
  screen.appendChild(questionCard);
  screen.appendChild(navButtons);
  container.appendChild(screen);

  return container;
}

// 创建雷达图 (使用 Canvas) - 动态绘制版本
function createRadarChart(scores: Record<string, number>): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 600;
  const ctx = canvas.getContext('2d')!;

  const centerX = 300;
  const centerY = 300;
  const maxRadius = 180;
  const levels = 5;

  // 颜色配置
  const colors = {
    red: '#E74C3C',
    blue: '#3498DB',
    yellow: '#F39C12',
    green: '#27AE60'
  };

  // Labels and colors
  const labelData = [
    { text: 'Flame', subtext: 'Power', color: '#FF6B9D' },
    { text: 'Deep Sea', subtext: 'Perfect', color: '#6B8EFF' },
    { text: 'Radiant', subtext: 'Vibrant', color: '#FFD93D' },
    { text: 'Forest', subtext: 'Peace', color: '#6BFFB8' }
  ];
  const angles = [0, 90, 180, 270].map(deg => (deg - 90) * Math.PI / 180);

  // 绘制背景网格
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.lineWidth = 1;

  for (let i = 1; i <= levels; i++) {
    const radius = (maxRadius / levels) * i;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();
  }

  // 绘制轴线
  angles.forEach(angle => {
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(
      centerX + Math.cos(angle) * maxRadius,
      centerY + Math.sin(angle) * maxRadius
    );
    ctx.stroke();
  });

  // 绘制标签 - 优化位置和对齐
  ctx.font = 'bold 18px -apple-system, sans-serif';

  labelData.forEach((data, i) => {
    const angle = angles[i];
    const distance = maxRadius + 60;
    const x = centerX + Math.cos(angle) * distance;
    const y = centerY + Math.sin(angle) * distance;

    // 根据位置调整对齐方式
    if (i === 0) { // 上方
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
    } else if (i === 1) { // 右方
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
    } else if (i === 2) { // 下方
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
    } else { // 左方
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
    }

    // 绘制主标签（带颜色）
    ctx.fillStyle = data.color;
    ctx.fillText(data.text, x, y);

    // 绘制副标签
    ctx.fillStyle = '#8E93A6';
    ctx.font = '14px -apple-system, sans-serif';
    if (i === 0) {
      ctx.fillText(data.subtext, x, y - 22);
    } else if (i === 1) {
      ctx.fillText(data.subtext, x + 48, y);
    } else if (i === 2) {
      ctx.fillText(data.subtext, x, y + 22);
    } else {
      ctx.fillText(data.subtext, x - 48, y);
    }
    ctx.font = 'bold 18px -apple-system, sans-serif';
  });

  // 计算最大分数用于归一化
  const maxScore = Math.max(...Object.values(scores));
  const normalizedScores = Object.values(scores).map(s => (s / maxScore) * maxRadius);

  // 动态绘制数据区域
  let progress = 0;
  const animate = () => {
    if (progress >= 1) return;

    progress += 0.02;
    const currentProgress = Math.min(progress, 1);

    // 使用缓动函数
    const eased = 1 - Math.pow(1 - currentProgress, 3);

    // 清除之前的数据区域（保留网格和标签）
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, maxRadius, 0, Math.PI * 2);
    ctx.clip();
    ctx.clearRect(centerX - maxRadius, centerY - maxRadius, maxRadius * 2, maxRadius * 2);
    ctx.restore();

    // 绘制网格（重新绘制）
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= levels; i++) {
      const radius = (maxRadius / levels) * i;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 绘制轴线
    angles.forEach(angle => {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + Math.cos(angle) * maxRadius,
        centerY + Math.sin(angle) * maxRadius
      );
      ctx.stroke();
    });

    // 绘制数据区域
    ctx.beginPath();
    normalizedScores.forEach((score, i) => {
      const angle = angles[i];
      const currentScore = score * eased;
      const x = centerX + Math.cos(angle) * currentScore;
      const y = centerY + Math.sin(angle) * currentScore;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.closePath();

    // 添加发光效果
    ctx.shadowBlur = 20;
    ctx.shadowColor = 'rgba(102, 126, 234, 0.8)';
    ctx.fillStyle = 'rgba(102, 126, 234, 0.3)';
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.strokeStyle = '#667eea';
    ctx.lineWidth = 3;
    ctx.stroke();

    // 绘制数据点
    const colorKeys = ['red', 'blue', 'yellow', 'green'] as const;
    normalizedScores.forEach((score, i) => {
      const angle = angles[i];
      const currentScore = score * eased;
      const x = centerX + Math.cos(angle) * currentScore;
      const y = centerY + Math.sin(angle) * currentScore;

      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fillStyle = colors[colorKeys[i]];
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.stroke();

      // 添加脉冲光晕
      if (eased > 0.8) {
        const pulseSize = 15 + Math.sin(Date.now() / 200 + i) * 5;
        ctx.beginPath();
        ctx.arc(x, y, pulseSize, 0, Math.PI * 2);
        ctx.strokeStyle = colors[colorKeys[i]];
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.3;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    });

    requestAnimationFrame(animate);
  };

  // 启动动画
  setTimeout(() => animate(), 300);

  return canvas;
}

// 创建结果屏幕
function createResultScreen(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'container';

  const screen = document.createElement('div');
  screen.className = 'result-screen';

  // Title
  const header = document.createElement('div');
  header.className = 'result-header';

  const title = document.createElement('h1');
  title.className = 'result-title';
  title.textContent = '🌟 Your Celestial Divination Results 🌟';

  const subtitle = document.createElement('p');
  subtitle.className = 'result-subtitle';
  subtitle.textContent = 'The stars have revealed your destiny\'s secret';

  header.appendChild(title);
  header.appendChild(subtitle);

  // Primary personality type
  const primaryType = state.getPrimaryType();
  const personality = personalityTypes[primaryType];

  const typeCard = document.createElement('div');
  typeCard.className = 'personality-type';

  const label = document.createElement('div');
  label.className = 'personality-label';
  label.textContent = '✨ Your Dominant Celestial Force ✨';

  const name = document.createElement('h2');
  name.className = 'personality-name';
  name.style.color = `var(--color-${personality.color})`;
  name.textContent = personality.name;

  const desc = document.createElement('p');
  desc.className = 'personality-description';
  desc.textContent = personality.description;

  typeCard.appendChild(label);
  typeCard.appendChild(name);
  typeCard.appendChild(desc);

  // Chart container
  const chartContainer = document.createElement('div');
  chartContainer.className = 'chart-container';

  const chartTitle = document.createElement('h3');
  chartTitle.className = 'chart-title';
  chartTitle.textContent = '⭐ Celestial Energy Distribution ⭐';

  const radarChartDiv = document.createElement('div');
  radarChartDiv.className = 'radar-chart';
  const canvas = createRadarChart(state.scores);
  radarChartDiv.appendChild(canvas);

  // Color bar chart
  const colorBars = document.createElement('div');
  colorBars.className = 'color-bars';

  const colorData = [
    { label: 'Flame', color: 'red', score: state.scores.red },
    { label: 'Deep Sea', color: 'blue', score: state.scores.blue },
    { label: 'Radiant', color: 'yellow', score: state.scores.yellow },
    { label: 'Forest', color: 'green', score: state.scores.green }
  ];

  const maxScore = Math.max(...colorData.map(c => c.score));

  colorData.forEach(({ label, color, score }) => {
    const bar = document.createElement('div');
    bar.className = 'color-bar';

    const barLabel = document.createElement('div');
    barLabel.className = `color-bar-label ${color}`;
    barLabel.textContent = label;

    const track = document.createElement('div');
    track.className = 'color-bar-track';

    const fill = document.createElement('div');
    fill.className = `color-bar-fill ${color}`;
    fill.style.width = '0%';

    // 动画效果
    setTimeout(() => {
      fill.style.width = `${(score / maxScore) * 100}%`;
    }, 100);

    track.appendChild(fill);

    const value = document.createElement('div');
    value.className = 'color-bar-value';
    value.textContent = score.toString();

    bar.appendChild(barLabel);
    bar.appendChild(track);
    bar.appendChild(value);
    colorBars.appendChild(bar);
  });

  chartContainer.appendChild(chartTitle);
  chartContainer.appendChild(radarChartDiv);
  chartContainer.appendChild(colorBars);

  // 性格特质
  const traitsGrid = document.createElement('div');
  traitsGrid.className = 'traits-grid';

  personality.traits.forEach(trait => {
    const card = document.createElement('div');
    card.className = `trait-card ${personality.color}`;

    const traitTitle = document.createElement('h4');
    traitTitle.className = 'trait-title';
    traitTitle.textContent = trait.title;

    const traitDesc = document.createElement('p');
    traitDesc.className = 'trait-description';
    traitDesc.textContent = trait.description;

    card.appendChild(traitTitle);
    card.appendChild(traitDesc);
    traitsGrid.appendChild(card);
  });

  // Action buttons
  const actionButtons = document.createElement('div');
  actionButtons.className = 'action-buttons';

  const retestBtn = document.createElement('button');
  retestBtn.className = 'action-button secondary';
  retestBtn.textContent = '🔮 Retake Test';
  retestBtn.onclick = () => {
    state.reset();
    render();
  };

  const shareBtn = document.createElement('button');
  shareBtn.className = 'action-button primary';
  shareBtn.textContent = '✨ Share Results';
  shareBtn.onclick = () => {
    // Create share text
    const shareText = `🌟 My Celestial Divination Result: ${personality.name}\n\n${personality.description}\n\n✨ Celestial Energy Distribution:\n🔥 Flame ${state.scores.red} | 💎 Deep Sea ${state.scores.blue} | ⭐ Radiant ${state.scores.yellow} | 🌿 Forest ${state.scores.green}`;

    // Try using Web Share API
    if (navigator.share) {
      navigator.share({
        title: 'Celestial Personality Divination Results',
        text: shareText
      }).catch(() => {
        copyToClipboard(shareText);
      });
    } else {
      copyToClipboard(shareText);
    }
  };

  // Helper function to copy to clipboard
  function copyToClipboard(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      showToast('✅ Results copied to clipboard!');
    } catch (err) {
      showToast('❌ Copy failed, please copy manually');
    }

    document.body.removeChild(textarea);
  }

  // Display toast message
  function showToast(message: string) {
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(26, 31, 58, 0.95);
      color: white;
      padding: 20px 40px;
      border-radius: 15px;
      font-size: 1.1rem;
      font-weight: 600;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
      z-index: 10000;
      animation: fadeIn 0.3s ease-out;
      border: 2px solid rgba(102, 126, 234, 0.5);
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'fadeOut 0.3s ease-out';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 2000);
  }

  actionButtons.appendChild(retestBtn);
  actionButtons.appendChild(shareBtn);

  screen.appendChild(header);
  screen.appendChild(typeCard);
  screen.appendChild(chartContainer);
  screen.appendChild(traitsGrid);
  screen.appendChild(actionButtons);
  container.appendChild(screen);

  return container;
}

// 渲染函数
function render() {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';

  // 添加背景动画
  app.appendChild(createBackground());

  // 根据状态渲染不同屏幕
  switch (state.currentScreen) {
    case 'welcome':
      app.appendChild(createWelcomeScreen());
      break;
    case 'question':
      app.appendChild(createQuestionScreen());
      break;
    case 'result':
      app.appendChild(createResultScreen());
      break;
  }
}

// 初始化应用
render();

// Vite HMR
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    render();
  });
}
